/**
 * Aviso de sessão que SOBREVIVE ao redirect para /login (at-22).
 *
 * O logout automático termina em `window.location.assign('/login')`, que recarrega
 * a aplicação — um `toast()` disparado antes disso morre junto com a página e o
 * usuário volta para o login sem saber por quê ("jogado pro alto do nada").
 * Por isso o motivo é gravado em `sessionStorage` e consumido pela tela de login
 * no primeiro mount depois do redirect.
 *
 * `sessionStorage` (e não `localStorage`) de propósito: o aviso é de uma aba/sessão
 * de navegação e não deve reaparecer semanas depois em outra aba.
 */

const CHAVE_AVISO = 'saac:aviso-sessao';

export type MotivoLogout =
  /** Sessão realmente expirou (refresh respondeu 401/403). */
  | 'expirada'
  /** O backend informou que a sessão foi encerrada por login em outro dispositivo. */
  | 'encerrada_outro_dispositivo';

const MENSAGENS: Record<MotivoLogout, string> = {
  expirada: 'Sua sessão expirou. Faça login novamente para continuar de onde parou.',
  encerrada_outro_dispositivo:
    'Sua sessão foi encerrada porque sua conta foi acessada em outro dispositivo. Faça login novamente.',
};

/**
 * Deduz o motivo a partir do corpo da resposta 401/403 do `/auth/refresh`.
 *
 * Hoje o backend não distingue os casos (o Plano A da at-22 é que vai passar a
 * distinguir), então o default é `expirada`. A detecção é permissiva de propósito
 * — reconhece um código conhecido OU uma mensagem que fale de outro dispositivo —
 * para funcionar sem depender do formato exato que o backend vai adotar.
 */
export function motivoDaRespostaRefresh(data: unknown): MotivoLogout {
  if (!data || typeof data !== 'object') return 'expirada';

  const corpo = data as { code?: unknown; codigo?: unknown; message?: unknown };
  const codigo = String(corpo.code ?? corpo.codigo ?? '').toUpperCase();
  if (
    codigo === 'SESSAO_ENCERRADA_OUTRO_DISPOSITIVO' ||
    codigo === 'REFRESH_TOKEN_ROTACIONADO'
  ) {
    return 'encerrada_outro_dispositivo';
  }

  const mensagem = String(corpo.message ?? '').toLowerCase();
  if (mensagem.includes('outro dispositivo')) return 'encerrada_outro_dispositivo';

  return 'expirada';
}

/** Grava o aviso ANTES do redirect. Nunca lança (storage pode estar bloqueado). */
export function gravarAvisoSessao(motivo: MotivoLogout): void {
  try {
    sessionStorage.setItem(CHAVE_AVISO, motivo);
  } catch {
    // modo privado / storage indisponível: seguimos sem o aviso
  }
}

/**
 * Lê e REMOVE o aviso (uso único) devolvendo a mensagem pronta para exibição.
 * Retorna `null` quando não há aviso pendente.
 */
export function consumirAvisoSessao(): string | null {
  try {
    const motivo = sessionStorage.getItem(CHAVE_AVISO);
    if (!motivo) return null;
    sessionStorage.removeItem(CHAVE_AVISO);
    return MENSAGENS[motivo as MotivoLogout] ?? MENSAGENS.expirada;
  } catch {
    return null;
  }
}
